import { useState, useEffect, useCallback } from 'react';
import ticketService from '../services/tickets';

export const useTickets = (skip = false) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [openCount, setOpenCount] = useState(0);

  const fetchTickets = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await ticketService.getTickets(params);
      setTickets(response.tickets || []);
      if (response.openCount !== undefined) {
        setOpenCount(response.openCount);
      }
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await ticketService.getStats();
      setStats(response);
      return response;
    } catch (err) {
      console.error('Failed to fetch ticket stats:', err);
      throw err;
    }
  }, []);

  const createTicket = useCallback(async (ticketData) => {
    try {
      const newTicket = await ticketService.createTicket(ticketData);
      setTickets(prev => [newTicket, ...prev]);
      await fetchStats();
      return newTicket;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [fetchStats]);

  const updateTicketStatus = useCallback(async (id, status, resolution = '') => {
    try {
      const updated = await ticketService.updateTicketStatus(id, status, resolution);
      setTickets(prev =>
        prev.map(t => (t._id === id ? updated : t))
      );
      await fetchStats();
      return updated;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [fetchStats]);

  const assignTicket = useCallback(async (id, userData) => {
    try {
      const updated = await ticketService.assignTicket(id, userData);
      setTickets(prev =>
        prev.map(t => (t._id === id ? updated : t))
      );
      return updated;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const addNote = useCallback(async (id, text) => {
    try {
      const updated = await ticketService.addNote(id, text);
      setTickets(prev =>
        prev.map(t => (t._id === id ? updated : t))
      );
      return updated;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const deleteTicket = useCallback(async (id) => {
    try {
      await ticketService.deleteTicket(id);
      setTickets(prev => prev.filter(t => t._id !== id));
      await fetchStats();
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [fetchStats]);

  // Initial fetch — skip jika belum login (parameter skip = true)
  useEffect(() => {
    if (skip) {
      setLoading(false);
      return;
    }
    const init = async () => {
      try {
        await Promise.all([fetchTickets(), fetchStats()]);
      } catch (err) {
        // silent
      }
    };
    init();
  }, [fetchTickets, fetchStats, skip]);

  const clearError = useCallback(() => setError(null), []);

  return {
    tickets,
    loading,
    error,
    stats,
    openCount,
    fetchTickets,
    fetchStats,
    createTicket,
    updateTicketStatus,
    assignTicket,
    addNote,
    deleteTicket,
    clearError
  };
};
